import React, { useState } from 'react';
import {
  Stack,
  Group,
  Text,
  ActionIcon,
  Alert,
  Progress,
  Card,
  Badge,
  Box,
  SimpleGrid,
  Skeleton,
  Modal,
  Title,
  Divider
} from '@mantine/core';
import {
  IconUpload,
  IconFile,
  IconTrash,
  IconDownload,
  IconFileText,
  IconFileX,
  IconTemplate
} from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showSuccessNotification, showErrorNotification, showApiErrorNotification } from '../../utils/notifications';
import { ActionButton } from '../common/ActionButton';
import { useGroupQuery } from '../../hooks/useGroupQuery';
import { reportsApi } from '../../api/reports';
import { UploadedFile } from '../../types/reports';

interface FileManagementProps {
  reportId: string;
  isReadOnly?: boolean;
  maxFileSize?: number; // in MB
  allowedTypes?: string[];
  showHeader?: boolean;
}

export function FileManagement({
  reportId,
  isReadOnly = false,
  maxFileSize = 10,
  allowedTypes = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.ppt', '.pptx', '.png', '.jpg', '.jpeg'],
  showHeader = true
}: FileManagementProps) {
  const queryClient = useQueryClient();
  
  // Fetch files using React Query
  const { data: files = [], isLoading: loadingFiles } = useGroupQuery(
    ['files', reportId],
    () => reportsApi.getFiles(reportId)
  );

  // Fetch template files using React Query
  const { data: templateData, isLoading: loadingTemplates } = useGroupQuery(
    ['templates', reportId],
    () => reportsApi.getTemplateFiles(reportId)
  );

  // Local state
  const [uploading, setUploading] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [fileInputKey, setFileInputKey] = useState(0);
  const [showOverwriteModal, setShowOverwriteModal] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [existingFileName, setExistingFileName] = useState<string>('');

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: ({ file, onProgress }: { file: File; onProgress: (progress: number) => void }) =>
      reportsApi.uploadFile(reportId, file, (progressEvent: any) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      }),
    onSuccess: (uploadedFile: UploadedFile) => {
      showSuccessNotification(`${uploadedFile.name} uploaded successfully`);
      // Invalidate and refetch files
      queryClient.invalidateQueries({ queryKey: ['files', reportId] });
    },
    onError: (error) => {
      showApiErrorNotification(error, 'Failed to upload file');
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (fileId: string) => reportsApi.deleteFile(reportId, fileId),
    onSuccess: (_, fileId) => {
      const deletedFile = (files as UploadedFile[]).find(f => f.id === fileId);
      if (deletedFile) {
        showSuccessNotification(`${deletedFile.name} deleted successfully`);
      }
      // Invalidate and refetch files
      queryClient.invalidateQueries({ queryKey: ['files', reportId] });
    },
    onError: (error) => {
      showApiErrorNotification(error, 'Failed to delete file');
    }
  });



  const performFileUpload = async (file: File) => {
    const lowercaseFileName = file.name.toLowerCase();
    const uploadId = Date.now().toString();
    
    setUploading(prev => [...prev, uploadId]);
    setUploadProgress(prev => ({ ...prev, [uploadId]: 0 }));

    try {
      // Create a new file with lowercase name
      const renamedFile = new File([file], lowercaseFileName, { type: file.type });
      
      // Use the upload mutation
      await uploadMutation.mutateAsync({
        file: renamedFile,
        onProgress: (progress: number) => {
          setUploadProgress(prev => ({ ...prev, [uploadId]: progress }));
        }
      });

      // Clear file input value and reset key to ensure onChange fires again  
      const fileInput = document.getElementById(`file-input-${fileInputKey}`) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
      setFileInputKey(prev => prev + 1);

    } catch (error) {
      // Error is already handled by the mutation
    } finally {
      // Clean up state
      setUploading(prev => prev.filter(id => id !== uploadId));
      
      // Remove progress after a short delay to show completion
      setTimeout(() => {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[uploadId];
          return newProgress;
        });
      }, 1000);
    }
  };

  const handleFileUpload = async (file: File | null) => {
    if (!file) return;

    // Validate file size
    if (file.size > maxFileSize * 1024 * 1024) {
      showErrorNotification(`File size must be less than ${maxFileSize}MB`);
      return;
    }

    // Validate file type
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      showErrorNotification(`File type not allowed. Supported types: ${allowedTypes.join(', ')}`);
      return;
    }

    // Check for duplicate file names (comparing lowercase since all files are stored lowercase)
    const lowercaseFileName = file.name.toLowerCase();
    const existingFile = (files as UploadedFile[]).find(f => f.name === lowercaseFileName);
    if (existingFile) {
      setPendingFile(file);
      setExistingFileName(existingFile.name);
      setShowOverwriteModal(true);
      return;
    }

    // No duplicate, proceed with upload
    await performFileUpload(file);
  };

  const handleOverwriteConfirm = async () => {
    if (pendingFile) {
      setShowOverwriteModal(false);
      
      // Upload the new file (GCS will automatically overwrite the existing file)
      await performFileUpload(pendingFile);
      
      setPendingFile(null);
      setExistingFileName('');
    }
  };

  const handleOverwriteCancel = () => {
    setShowOverwriteModal(false);
    setPendingFile(null);
    setExistingFileName('');
    
    // Clear file input value and reset key to ensure onChange fires again
    const fileInput = document.getElementById(`file-input-${fileInputKey}`) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    setFileInputKey(prev => prev + 1);
  };

  const handleFileDelete = async (fileId: string) => {
    deleteMutation.mutate(fileId);
  };



  // File download mutation
  const fileDownloadMutation = useMutation({
    mutationFn: ({ fileId, fileName }: { fileId: string; fileName: string }) => reportsApi.getFileDownloadUrl(reportId, fileId),
    onSuccess: (downloadResponse, { fileId, fileName }) => {
      if (downloadResponse.download_url) {
        // Create download link
        const link = document.createElement('a');
        link.href = downloadResponse.download_url;
        link.download = fileName;
        link.target = '_blank';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        showErrorNotification('Download URL not available');
      }
    },
    onError: (error) => {
      showApiErrorNotification(error, 'Failed to download file');
    }
  });

  const handleFileDownload = (file: UploadedFile) => {
    if (!file.id) {
      console.error('File ID is missing:', file.id);
      showErrorNotification('File download not available - missing file ID');
      return;
    }

    fileDownloadMutation.mutate({ fileId: file.id, fileName: file.name });
  };

  // Template download mutation
  const templateDownloadMutation = useMutation({
    mutationFn: ({ filename }: { filename: string }) => reportsApi.downloadTemplateFile(reportId, filename),
    onSuccess: (blob: Blob, { filename }: { filename: string }) => {
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSuccessNotification(`Template ${filename} downloaded successfully`);
    },
    onError: (error, { filename }: { filename: string }) => {
      showApiErrorNotification(error, `Failed to download template ${filename}`);
    }
  });

  const handleTemplateDownload = (filename: string) => {
    templateDownloadMutation.mutate({ filename });
  };

  const getFileIcon = (file: UploadedFile) => {
    const contentType = file.contentType || '';
    const extension = file.type || '';
    const iconStyle = { color: 'var(--mantine-color-dimmed)' };
    
    // Check content type first, then fall back to extension
    if (contentType.includes('pdf') || extension.includes('pdf')) return <IconFileText style={iconStyle} />;
    if (contentType.includes('document') || contentType.includes('word') || extension.includes('doc')) return <IconFileText style={iconStyle} />;
    if (contentType.includes('sheet') || contentType.includes('excel') || extension.includes('xls')) return <IconFileText style={iconStyle} />;
    if (contentType.includes('presentation') || contentType.includes('powerpoint') || extension.includes('ppt')) return <IconFileText style={iconStyle} />;
    if (contentType.includes('text/csv') || extension.includes('csv')) return <IconFileText style={iconStyle} />;
    if (contentType.includes('application/json') || extension.includes('json')) return <IconFileText style={iconStyle} />;
    if (contentType.includes('image/') || extension.includes('png') || extension.includes('jpg') || extension.includes('jpeg')) return <IconFile style={iconStyle} />;
    return <IconFile style={iconStyle} />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    // Force UTC parsing by ensuring timezone info exists
    let utcString = dateString;
    
    // Check if timestamp already has timezone info (Z, +offset, or -offset)
    const hasTimezone = /[Z+-]\d{2}:?\d{2}?$/.test(dateString) || dateString.endsWith('Z');
    
    if (!hasTimezone) {
      // Remove microseconds if present and add 'Z' to force UTC parsing
      utcString = dateString.replace(/\.\d+$/, '') + 'Z';
    }
    
    const date = new Date(utcString);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const renderFileSkeletons = () => (
    <SimpleGrid
        cols={1}
        spacing="xs"
        breakpoints={[
          { maxWidth: 'md', cols: 1 }
        ]}
      >
        {[...Array(3)].map((_, index) => (
          <Card key={index} padding="sm" withBorder>
            <Group position="apart" noWrap>
              <Group spacing="sm" sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton height={24} width={24} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Skeleton height={16} width="60%" mb={4} />
                  <Group spacing="md">
                    <Skeleton height={12} width={60} />
                    <Skeleton height={12} width={80} />
                  </Group>
                </Box>
              </Group>
              <Group spacing="xs">
                <Skeleton height={24} width={24} />
                <Skeleton height={24} width={24} />
                <Skeleton height={24} width={24} />
              </Group>
            </Group>
          </Card>
        ))}
      </SimpleGrid>
  );

  return (
    <Stack spacing="md">
      {showHeader && (
        <>
          <Group position="apart">
            <Text size="lg" weight={600}>
              Supporting Documents
            </Text>
            <Badge variant="light" color="gray">
              {files.length} file{files.length !== 1 ? 's' : ''}
            </Badge>
          </Group>

          <Text size="sm" color="dimmed">
            Upload supporting documents, attachments, or evidence related to this report.
          </Text>
          <Text size="xs" color="dimmed">
            Accepted formats: {allowedTypes.join(', ')} (max {maxFileSize}MB each)
          </Text>
        </>
      )}



      {!isReadOnly && (
        <>
          <Box
            sx={(theme) => ({
              position: 'relative',
              border: `2px dashed ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[4]}`,
              borderRadius: theme.radius.md,
              padding: theme.spacing.xl,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
              '&:hover': {
                borderColor: theme.colors.blue[theme.colorScheme === 'dark' ? 4 : 5],
                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.blue[0],
                transform: 'translateY(-1px)',
                boxShadow: theme.shadows.sm
              },
              '&:active': {
                transform: 'translateY(0)',
                boxShadow: 'none'
              }
            })}
            onClick={() => {
              const fileInput = document.getElementById(`file-input-${fileInputKey}`) as HTMLInputElement;
              if (fileInput) {
                fileInput.click();
              }
            }}
          >
            <input
              id={`file-input-${fileInputKey}`}
              type="file"
              accept={allowedTypes.join(',')}
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                handleFileUpload(file);
              }}
              style={{ display: 'none' }}
              disabled={uploading.length > 0}
            />
            
            <Stack spacing="sm" align="center">
              <Box
                sx={(theme) => ({
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.blue[1],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  '& svg': {
                    color: theme.colorScheme === 'dark' ? theme.colors.blue[4] : theme.colors.blue[6]
                  }
                })}
              >
                <IconUpload size={24} />
              </Box>
              
              <div>
                <Text size="md" weight={600}>
                  {uploading.length > 0 ? 'Uploading...' : 'Upload Files'}
                </Text>
                <Text size="sm" color="dimmed" mt={4}>
                  Click to browse or drag and drop files here
                </Text>
              </div>
              
              <Group spacing="xs">
                <Badge variant="dot" color="blue" size="sm">
                  {allowedTypes.join(', ').replace(/\./g, '').toUpperCase()}
                </Badge>
                <Badge variant="dot" color="gray" size="sm">
                  Max {maxFileSize}MB
                </Badge>
              </Group>
            </Stack>
          </Box>

          {/* Upload Progress */}
          {Object.entries(uploadProgress).map(([uploadId, progress]) => (
            <Card 
              key={uploadId} 
              padding="md" 
              withBorder
              sx={(theme) => ({
                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
                borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]
              })}
            >
              <Stack spacing="xs">
                <Group position="apart">
                  <Group spacing="xs">
                    <Box sx={(theme) => ({
                      '& svg': {
                        color: theme.colorScheme === 'dark' ? theme.colors.blue[4] : theme.colors.blue[6]
                      }
                    })}>
                      <IconUpload size={16} />
                    </Box>
                    <Text size="sm" weight={500}>Uploading file...</Text>
                  </Group>
                  <Text size="sm" color="dimmed">{progress}%</Text>
                </Group>
                <Progress 
                  value={progress} 
                  size="lg" 
                  radius="md"
                  animate
                  color={progress === 100 ? 'green' : 'blue'}
                />
              </Stack>
            </Card>
          ))}
        </>
      )}

      {loadingFiles ? (
        renderFileSkeletons()
      ) : files.length === 0 && uploading.length === 0 ? (
        <Alert
          icon={<IconFileX size={16} />}
          title="No files uploaded"
          color="gray"
          variant="light"
        >
          {isReadOnly 
            ? "No supporting documents have been uploaded for this report."
            : "Upload files to provide additional documentation for your report."
          }
        </Alert>
      ) : (
        <SimpleGrid
            cols={1}
            spacing="xs"
            breakpoints={[
              { maxWidth: 'md', cols: 1 }
            ]}
          >
            {(files as UploadedFile[]).map((file) => (
              <Card 
                key={file.id} 
                padding="sm" 
                withBorder
                sx={(theme) => ({
                  backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
                  borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3],
                  '&:hover': {
                    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0]
                  }
                })}
              >
                <Group position="apart" noWrap>
                  <Group spacing="sm" sx={{ flex: 1, minWidth: 0 }}>
                    {getFileIcon(file)}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" weight={500} truncate>
                        {file.name}
                      </Text>
                      <Group spacing="md">
                        <Text size="xs" color="dimmed">
                          {formatFileSize(file.size)}
                        </Text>
                        <Text size="xs" color="dimmed">
                          {formatDate(file.uploadDate)}
                        </Text>
                      </Group>
                    </Box>
                  </Group>
                  
                  <Group spacing="xs">
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      onClick={() => handleFileDownload(file)}
                      title="Download file"
                      loading={fileDownloadMutation.isPending}
                      disabled={fileDownloadMutation.isPending}
                    >
                      <IconDownload size={16} />
                    </ActionIcon>
                    
                    {!isReadOnly && (
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="red"
                        onClick={() => handleFileDelete(file.id)}
                        title="Delete file"
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    )}
                  </Group>
                </Group>
              </Card>
            ))}
          </SimpleGrid>
      )}

      {/* Divider */}
      {!isReadOnly && ((templateData?.files && templateData.files.length > 0) || loadingTemplates) && (
        <Divider mt="xl" />
      )}

      {/* Templates Section - positioned below uploads (edit mode only) */}
      {!isReadOnly && ((templateData?.files && templateData.files.length > 0) || loadingTemplates) && (
        <Stack spacing="md" mt="md">
          {showHeader && (
            <Group position="apart">
              <Text size="md" weight={600}>
                Templates
              </Text>
              <Badge variant="light" color="blue">
                {templateData?.files?.length || 0} template{templateData?.files?.length !== 1 ? 's' : ''}
              </Badge>
            </Group>
          )}

          <Text size="sm" color="dimmed">
            Templates you can use to upload supporting evidence
          </Text>

          {loadingTemplates ? (
            <SimpleGrid cols={1} spacing="xs">
              {[...Array(2)].map((_, index) => (
                <Card key={index} padding="sm" withBorder>
                  <Group position="apart" noWrap>
                    <Group spacing="sm" sx={{ flex: 1, minWidth: 0 }}>
                      <Skeleton height={24} width={24} />
                      <Skeleton height={16} width="60%" />
                    </Group>
                    <Skeleton height={24} width={24} />
                  </Group>
                </Card>
              ))}
            </SimpleGrid>
          ) : (
            <SimpleGrid cols={1} spacing="xs">
              {templateData?.files?.map((templateFile) => (
                <Card 
                  key={templateFile.name} 
                  padding="sm" 
                  withBorder
                  sx={(theme) => ({
                    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.blue[0],
                    borderColor: theme.colorScheme === 'dark' ? theme.colors.blue[8] : theme.colors.blue[3],
                    '&:hover': {
                      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.blue[1]
                    }
                  })}
                >
                  <Group position="apart" noWrap>
                    <Group spacing="sm" sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={(theme) => ({
                        '& svg': {
                          color: theme.colorScheme === 'dark' ? theme.colors.blue[4] : theme.colors.blue[6]
                        }
                      })}>
                        <IconTemplate size={20} />
                      </Box>
                      <Text size="sm" weight={500} truncate>
                        {templateFile.name}
                      </Text>
                      <Badge size="xs" variant="light" color="blue">
                        Template
                      </Badge>
                    </Group>
                    
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="blue"
                      onClick={() => handleTemplateDownload(templateFile.name)}
                      title={`Download ${templateFile.name} template`}
                      loading={templateDownloadMutation.isPending}
                      disabled={templateDownloadMutation.isPending}
                    >
                      <IconDownload size={16} />
                    </ActionIcon>
                  </Group>
                </Card>
              ))}
            </SimpleGrid>
          )}
        </Stack>
      )}

      {/* File Overwrite Confirmation Modal */}
      <Modal
        opened={showOverwriteModal}
        onClose={handleOverwriteCancel}
        closeButtonProps={{ tabIndex: -1 }}
        closeOnClickOutside={true}
        title={
          <Title order={3} sx={(theme) => ({
            color: theme.colorScheme === 'dark' ? theme.colors.red[4] : theme.colors.red[7],
            marginBottom: theme.spacing.xs
          })}>
            File Already Exists
          </Title>
        }
        centered
        role="dialog"
        aria-label="File Already Exists"
        styles={(theme) => ({
          header: {
            marginBottom: 0,
            paddingBottom: 0
          }
        })}
      >
        <Stack>
          <Text size="sm">
            A file named <Text component="span" weight={600}>"{existingFileName}"</Text> already exists. 
            Do you want to overwrite it with the new file?
          </Text>
          
          <Group position="apart">
            <ActionButton
              onClick={handleOverwriteCancel}
              label="Cancel"
              ariaLabel="Cancel file overwrite"
              actionType="secondary"
            />
            <ActionButton
              onClick={handleOverwriteConfirm}
              label="Overwrite File"
              ariaLabel="Overwrite the existing file"
              actionType="primary"
            />
          </Group>
        </Stack>
      </Modal>

    </Stack>
  );
} 